import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import FormField from "@/components/Forms/FormField";
import {
  ProductionSceneActorResource,
  ProductionSceneResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";

type FormDataT = {
  name: string;
  role: string;
  date_time?: string;
  note?: string;
  production_scene_id: number;
};

interface Props {
  onClose: () => void;
  scenes?: number[];
  actor?: ProductionSceneActorResource;
  scene?: ProductionSceneResource;
}

const ActorsModal: FC<Props> = ({ onClose, scenes, actor, scene }) => {
  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: actor?.name ?? "",
      role: actor?.role ?? "",
      date_time: actor?.time ?? "",
      note: actor?.note ?? "",
      production_scene_id: scene?.id,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (actor) {
          await api.put<never, { id: number }>(
            `/v1/production/scenes/actors/${actor.id}`,
            data
          );
        } else {
          await api.post<never, ProductionSceneActorResource>(
            "/v1/production/scenes/actors",
            data
          );
        }

        queryClient.invalidateQueries(["production_projects", { projectId }]);

        alerts.success(
          `Actor has been successfully ${!!actor ? "updated" : "created"}
          `
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${!actor ? "Add" : "Edit"} actors & crafters`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="name"
                label="Assignee"
                id="name"
                suggestionsEndpoint="/v1/production/scenes/actors/names"
              />
              {!!scenes?.length && (
                <FormField<FormDataT, number>
                  control={control}
                  name="production_scene_id"
                  label="Scene number"
                  id="scene_id"
                  type="select"
                  options={scenes.map((scene, index) => {
                    return {
                      label: (index + 1).toString(),
                      value: scene,
                    };
                  })}
                />
              )}
              <FormField<FormDataT>
                control={control}
                name="role"
                label="Role"
                id="role"
                suggestionsEndpoint="/v1/production/scenes/actors/roles"
              />
              <FormField<FormDataT>
                control={control}
                name="date_time"
                label="Start Date & Time"
                id="date"
                type="datepicker"
                datepickerType="datetime"
              />
              <FormField<FormDataT>
                control={control}
                name="note"
                label="Notes"
                type="textarea"
                id="notes"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ActorsModal;
